<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <div
    class="promo-banner critical-component-hide"
    :style="`--autoscroll-timing: ${autoscroll.timing}s;`"
    @focusin="state = 'paused'"
    @focusout="state = 'playing'"
    @mouseenter="state = 'paused'"
    @mouseleave="state = 'playing'"
    @touchstart.passive="state = 'paused'"
    @touchend="state = 'playing'"
  >
    <span
      class="visually-hidden"
      v-text="$string('general.site_header.promo_banner')"
    />

    <ul
      ref="list"
      class="promo-banner__list"
    >
      <li
        v-for="(block, index) of blocks"
        ref="slide"
        :key="`promo-${index}`"
        class="promo-banner__list-item text-body-xs-regular-desktop text-body-2xs-regular"
        v-html="getBlockText(block.title)"
      />
    </ul>
  </div>
</template>

<script>

/**
 * Vue: Promo banner (promo-banner)
 * -----------------------------------------------------------------------------
 * Promo banner template.
 * - Conditionally loaded when section has blocks.
 * - Scroll-snap is used for basic scrolling and JS is used to scroll slides.
 * - For more advanced functionality add carousel.
 *
 * @param {Object} autoscroll - Autoscroll settings.
 * @param {Array} blocks - Promo blocks.
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 *
 */
import { isVisible } from '~/helpers/general'

export default {
  name: 'PromoBanner',

  props: {
    autoscroll: {
      type: Object,
      default: () => ({
        enable: false,
        timing: 5,
      }),
    },
    blocks: {
      type: Array,
      default: () => [],
    },
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
  },

  data() {
    return {
      currentSlide: 1,
      interval: {},
      state: 'playing',
    }
  },

  async mounted() {
    await this.$nextTick()
    cnvs.EventBus.emit('cnvs:update-site-header-height')
    this.setAutoscrollAnimation()
  },

  unmounted() {
    clearInterval(this.interval)
  },

  methods: {

    /**
     * Get block text.
     * - Applies text class to <a> elements.
     * @param {String} block - Block text.
     * @returns {String}
     */
    getBlockText(block) {
      return block
        .replaceAll('<a', '<a class="text-body-xs-underline-desktop text-body-2xs-underline"')
        .replaceAll('<strong', '<strong class="text-body-xs-bold-desktop text-body-2xs-bold"')
    },

    /**
     * Set autoscroll animation.
     */
    setAutoscrollAnimation() {
      if (!this.autoscroll.enable) {
        return
      }

      /**
       * Disable smooth scrolling when reduced motion preference is set.
       */
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth'

      /**
       * Set is visible options.
       */
      const options = {
        container: this.$refs.list,
        direction: 'horizontal',
      }

      this.interval = setInterval(() => {
        if (this.state === 'paused') {
          return
        }

        /**
         * If current slide isn't visible then user has interacted manually.
         * - Cancel animation.
         */
        if (!isVisible(this.$refs.slide[this.currentSlide - 1], options)) {
          this.state = 'paused'
          clearInterval(this.interval)
          return
        }

        this.currentSlide = this.currentSlide === this.blocks.length
          ? 1
          : this.currentSlide + 1

        this.$refs.slide[this.currentSlide - 1].scrollIntoView({ behavior })
      }, this.autoscroll.timing * 1000)
    },
  },
}
</script>

<style lang="scss">
@import './promo-banner';
</style>
